import type { BoardingPassDownloadPathParams } from '@models/api/boarding-pass.model';

/**
 * Returns the env value or an empty string.
 * Base URLs are intentionally empty in development — the Vite dev server
 * reverse proxy routes /v1 and /tridionfeed to the correct upstream host.
 */
export const optionalEnv = (key: string, fallback = ''): string =>
  (import.meta.env[key] as string | undefined) ?? fallback;

const stripLeadingSlashes = (value: string): string => {
  let start = 0;
  while (start < value.length && value.charAt(start) === '/') {
    start += 1;
  }
  return value.slice(start);
};

const stripTrailingSlashes = (value: string): string => {
  let end = value.length;
  while (end > 0 && value.charAt(end - 1) === '/') {
    end -= 1;
  }
  return value.slice(0, end);
};

const joinBaseUrl = (host: string, path: string): string => {
  if (!host) {
    return path;
  }

  if (!path) {
    return host;
  }

  return `${stripTrailingSlashes(host)}/${stripLeadingSlashes(path)}`;
};

const contentBaseUrl = optionalEnv('VITE_CONTENT_URL');
const apiBaseRoute = optionalEnv('VITE_API_BASE_URL');

export const API_CONFIG = {
  BASE_URL: joinBaseUrl(contentBaseUrl, apiBaseRoute),
  TRANSLATIONS_BASE_URL: optionalEnv('VITE_TRANSLATIONS_BASE_URL'),
  MASTER_DATA_BASE_URL: optionalEnv('VITE_MASTER_DATA_BASE_URL'),
  TIMEOUT_MS: 30_000,
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY_MS: 1_000,
} as const;

export const API_ENDPOINTS = {
  MASTER_DATA: (lang: string): string => `/tridionfeed/${lang}/include/json/airportlist.json`,
  COUNTRY_LIST: (lang: string): string => `/tridionfeed/${lang}/include/json/countrylist.json`,
  GROUPED_STATES: (lang: string): string => `/tridionfeed/${lang}/include/json/grouped-states.json`,
  CURRENCIES: (lang: string): string => `/tridionfeed/${lang}/include/json/currencies.json`,
  QUESTIONNAIRE: (lang: string): string =>
    `/tridionfeed/${lang}/include/json/olci/d/questionnaire.json`,
  CODE_TYPE_LIST: (lang: string): string => `/tridionfeed/${lang}/include/json/codetypelist.json`,
  MEALS_LIST: (lang: string): string => `/tridionfeed/${lang}/include/json/meals.json`,
  EQUIPMENT_TYPES: (lang: string): string =>
    `/tridionfeed/${lang}/include/json/equipment-types.json`,
  FARE_BRANDS: (lang: string): string => `/tridionfeed/${lang}/include/json/farebrands.json`,
  EXCEPTION_MESSAGES: (lang: string): string =>
    `/tridionfeed/${lang}/include/json/olci/exception-messages.json`,
  ROUTE_MESSAGES: (lang: string): string =>
    `/tridionfeed/${lang}/include/json/olci/routemessages.json`,
  ANNOUNCEMENTS: (lang: string): string =>
    `/tridionfeed/${lang}/include/json/olci/annoucement.json`,
  OLCI_BANNERS: (lang: string): string =>
    `/tridionfeed/${lang}/include/json/olci/d/olci-banners.json`,
  OFFERS: (lang: string): string => `/tridionfeed/${lang}/include/json/olci/d/offer-carousels.json`,
  LIFE_BANNERS: (): string => `/tridionfeed/include/json/eshop.json`,
  UPGRADE_OFFER: (lang: string): string =>
    `/tridionfeed/${lang}/include/json/olci/d/businessupgrade.json`,
  DETAIL_UPGRADE: (lang: string): string => `/tridionfeed/${lang}/include/json/olci/d/upsell.json`,
  RANDOM_SEAT_SELECTION_RULES: (lang: string): string =>
    `/tridionfeed/${lang}/include/json/olci/randomseatselection.json`,
  APP_SETTINGS: (lang: string): string =>
    `/tridionfeed/${lang}/include/json/olci/d/application-settings.json`,
  TRANSLATIONS: (lang: string): string => `/${lang}/include/json/olci/d/resources.json`,
  CARRIERS: (lang: string): string => `/tridionfeed/${lang}/include/json/carriers.json`,
  RESOURCE_LIST: (lang: string): string =>
    `/tridionfeed/${lang}/include/json/olci/resource-list.json`,
  DOCUMENT_LIST: (lang: string): string => `/tridionfeed/${lang}/include/json/documentlist.json`,
  PNR_RETRIEVE: '/Pnr/retrieve',
  PNR_CONFIRM: '/Pnr/confirm',
  PNR_VALIDATE: (pnr: string): string => `/Pnr/validate/${pnr}`,
  PNR_VALIDATE_GROUP: '/Pnr/validategroup',
  PNR_NAME_CHECK: '/Pnr/namecheck',
  EXTRAS_OFFERS: '/Extras/offers',
  SPECIAL_SERVICE: '/SpecialService',
  EXTRAS: {
    GET: '/Extras',
    SEAT_OFFERS: '/Extras/seatoffers',
    BAGGAGE_OFFERS: '/Extras/baggageoffers',
    MEAL_OFFERS: '/Extras/mealoffers',
    CHANGE: '/Extras/change',
    ADD_ITEM: '/Extras/Add',
    REMOVE_ITEM: '/Extras/remove',
    ADD_ASSIST: '/Extras/Addssrs',
    REMOVE_SSRS: '/Extras/removessrs',
  },
  SELECT_PAX: '/Passenger/SelectPax',
  ADD_FFP: '/Passenger/AddFFP',
  CHECKIN: '/Passenger/checkin',
  CHECKIN_WITHOUT_SSR: '/Passenger/checkinWithoutSSR',
  OFFLOAD_PAX: '/Passenger/Offload',
  PAYMENT: {
    PROCESS: '/Payment/process',
    VALIDATE: '/Payment/validate',
    UPGRADE: '/Payment/Upgrade',
  },
  UPDATE_PASSENGER: '/Passenger/update',
  UPDATE_PAX_CONTACT_INFO: '/Passenger/UpdatePaxContactInfo',
  TIM: (timaticBaseUrl: string, lang: string): string =>
    joinBaseUrl(timaticBaseUrl, `/${lang}/Form/TimaticQuery`),
  NOTIFICATION: {
    SMS: '/Notification/SMS',
    EMAIL: '/Notification/Email',
    EMAIL_ALL: '/Notification/EmailAll',
    SMS_ALL: '/Notification/SmsAll',
    WAPP_ALL: '/Notification/WAPPAll',
    WAPP: '/Notification/WAPP',
  },
  BOARDING_PASS: {
    DOWNLOAD: (params: BoardingPassDownloadPathParams): string =>
      `/BoardingPass/download/${params.logicalFlightId}/${params.physicalFlightId}/${params.resPaxId}/${encodeURIComponent(params.paxJourneyId)}`,
  },
  TERMINALS: (lang: string): string => `/tridionfeed/${lang}/include/json/terminal-display.json`,
  PREFERENCES: '/Preferences',
} as const;
